import {DATE_VALIDATION_TYPE} from '@/utils/constants/commonConstants'
import {COUNTRY_CODES} from '@/utils/constants/globalConstants'
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants'

export const dateJsonSchema = {
  type: 'object',
  properties: {
    targetType: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      options: [],
    },
  },
  required: [],
}

export const dateUiSchema = targetType => ({
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'setup-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/targetType',
          component: 'dropdownControl',
          placeholder: 'Select Target Type',
          label: 'Target Type *',
          className: 'dropdown-width text-sm',
          isDark: true,
          errorMessage: 'This field is required',
          value: targetType,
        },
      ],
    },
  ],
})

export const targetNameJsonSchema = {
  type: 'object',
  properties: {
    targetName: {
      type: 'string',
    },
  },
  required: [],
}

export const targetNameUiSchema = {
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'setup-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/targetName',
          component: 'textfield',
          placeholder: 'Add Target Name',
          label: 'Target Name *',
          className: 'box-size-width text-sm',
          isDark: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'noSpecialChar', msg: `Please use alphanumeric values to enter a valid name`, excludeLabel: true},
          ],
        },
      ],
    },
  ],
}

export const pointDisplayJsonSchema = {
  type: 'object',
  properties: {
    pointDisplay: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      options: [
        {
          id: 1,
          displayText: 'Cumulative',
        },
        {
          id: 2,
          displayText: 'Individual',
        },
      ],
    },
  },
  required: [],
}

export const pointDisplayUiSchema = {
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'point-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/pointDisplay',
          component: 'dropdownControl',
          label: 'Point Display',
          placeholder: 'Choose Point Display',
          className: 'box-size-width text-sm',
          isDark: true,
        },
      ],
    },
  ],
}

const durationOptions = [
  {value: 'MONTHLY', displayText: 'Monthly'},
  {value: 'QUARTERLY', displayText: 'Quarterly'},
  {value: 'ANNUAL', displayText: 'Annual'},
  {value: 'CUSTOM', displayText: 'Custom'},
]

// Combined JSON Schema for single JsonFormsWrapper
export const availabilityWithDisplayJsonSchema = (countryCode, salesTargetType, formData) => {
  const base = availibilityJsonSchema(countryCode, salesTargetType)
  if (!formData?.activeDisplay) {
    // Merge properties and required arrays
    return {
      ...base,
      properties: {
        ...base.properties,
        ...activeAndDisplayJsonSchema.properties,
      },
      required: [...(base.required || []), ...(activeAndDisplayJsonSchema.required || [])],
    }
  }
  return base
}

// Combined UI Schema for single JsonFormsWrapper
export const availabilityWithDisplayUiSchema = (countryCode, salesTargetType, startDate, endDate, formData) => {
  const base = availibilityUiSchema(countryCode, salesTargetType, startDate, endDate, formData)
  if (!formData?.activeDisplay) {
    const displayUi = activeAndDisplayUiSchema(
      formData?.displayStartDate || '',
      formData?.displayEndDate,
      formData?.startDate,
      formData?.endDate
    )
    // Merge by appending display fields after base elements
    return {
      type: 'VerticalLayout',
      className: 'filters-partners',
      elements: [...(base.elements || []), ...(displayUi.elements || [])],
    }
  }
  return base
}

export const availibilityJsonSchema = (countryCode, salesTargetType) => {
  const isEgyptAndApplicableTarget =
    countryCode === COUNTRY_CODES.EGYPT &&
    (salesTargetType === TARGET_TYPES.MainTarget || salesTargetType === TARGET_TYPES.ProductTarget)

  return {
    type: 'object',
    properties: {
      startDate: {
        type: 'string',
      },
      endDate: {
        type: 'string',
      },
      ...(isEgyptAndApplicableTarget && {
        duration: {
          type: 'string',
          options: durationOptions,
        },
      }),
      activeDisplay: {type: 'boolean'},
    },
    required: [],
  }
}

export const availibilityUiSchema = (countryCode, salesTargetType, startDate, endDate, formData) => {
  const isEgyptAndApplicableTarget =
    countryCode === COUNTRY_CODES.EGYPT &&
    (salesTargetType === TARGET_TYPES.MainTarget || salesTargetType === TARGET_TYPES.ProductTarget)

  const startDateElement = {
    type: 'Control',
    scope: '#/properties/startDate',
    component: 'datepicker',
    className: 'box-size-width z-50',
    placeholder: 'Choose date',
    label: 'Active Start Date *',
    dependentDate: endDate,
    isDark: true,
    required: false, // Corresponds to UI indication, not schema validation 'required' array
    validationRules: [
      {type: 'required', msg: ` is required`},
      {
        type: DATE_VALIDATION_TYPE.START_DATE,
        msg: ` Target can only start from a date in future`,
        excludeLabel: true,
      },
      {
        type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
        msg: ` Target can only start from a date in future`,
        endDate: endDate,
        excludeLabel: true,
      },
    ],
  }

  const durationElement = {
    type: 'Control',
    scope: '#/properties/duration',
    label: 'Duration *',
    component: 'dropdownControl',
    className: 'box-size-width mt-1 z-45',
    isDark: true,
    required: true, // UI indication and for form logic
  }

  let endDateDisabled
  let endDateHelperText = null

  if (isEgyptAndApplicableTarget) {
    // For Egypt applicable targets, endDate visibility and editability depend on duration
    if (formData?.durationValue === 'CUSTOM') {
      endDateDisabled = !startDate // Editable if startDate is present
      if (!startDate) {
        endDateHelperText = 'Please select start date before selecting end date.'
      }
    } else {
      // No duration selected yet, endDate field will be hidden, but set disabled state for completeness
      endDateDisabled = true
    }
  } else {
    // For non-Egypt or non-applicable targets
    endDateDisabled = !startDate
    if (!startDate) {
      endDateHelperText = 'Please select start date before selecting end date.'
    }
  }

  const endDateElement = {
    type: 'Control',
    scope: '#/properties/endDate',
    component: 'datepicker',
    placeholder: 'Choose date',
    className: 'box-size-width z-45',
    label: 'Active End Date *',
    dependentDate: startDate,
    disabled: endDateDisabled,
    disabledHelperText: endDateHelperText,
    isDark: true,
    required: false, // Corresponds to UI indication
    validationRules: [
      {type: 'required', msg: ` is required`},
      {
        type: DATE_VALIDATION_TYPE.END_DATE,
        msg: ` Target cannot end before start date, please select correct values`,
        startDate: startDate,
        excludeLabel: true,
      },
    ],
  }

  let firstRowElements
  if (isEgyptAndApplicableTarget) {
    firstRowElements = [startDateElement, durationElement]
    if (formData?.duration) {
      firstRowElements.push(endDateElement)
    }
  } else {
    firstRowElements = [startDateElement, endDateElement]
  }

  return {
    type: 'VerticalLayout',
    className: 'filters-partners',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'avail-first-row',
        elements: firstRowElements,
      },
      {
        type: 'HorizontalLayout',
        className: 'avail-second-row',
        elements: [
          {
            type: 'Control',
            scope: '#/properties/activeDisplay',
            component: 'toggle',
            className: 'mt-4',
            label: 'Active and Display the same?',
            isDark: true,
            showCheckedIcon: false,
          },
        ],
      },
    ],
  }
}

export const displayJsonSchema = {
  type: 'object',
  properties: {
    targetEndDate: {
      type: 'string',
    },
  },
  required: [],
}

export const displayUiSchema = startDate => ({
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'display-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/targetEndDate',
          component: 'datepicker',
          className: 'box-size-width z-19',
          placeholder: 'Choose date',
          label: 'Target Delivery Date *',
          isDark: true,
          required: false,
          dependentDate: startDate,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {
              type: DATE_VALIDATION_TYPE.START_DATE,
              msg: ` Target delivery date should be after target end date`,
              excludeLabel: true,
            },
            {
              type: DATE_VALIDATION_TYPE.START_DATE_GREATER,
              endDate: startDate,
              msg: ' Target delivery date should be after active end date',
              excludeLabel: true,
            },
          ],
        },
      ],
    },
  ],
})
export const awardJsonSchema = {
  type: 'object',
  properties: {
    awardPoint: {
      type: 'boolean',
    },
  },
  required: [],
}

export const awardUiSchema = {
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'display-second-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/awardPoint',
          component: 'toggle',
          label: 'Award points immediately when goal hit?',
          isDark: true,
          showCheckedIcon: false,
        },
      ],
    },
  ],
}

export const pointJsonSchema = {
  type: 'object',
  properties: {
    pointsDeliveryDate: {type: 'string'},
  },
  required: [],
}
export const pointUiSchema = (pointsDeliveryDate, targetEndDate) => ({
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'display-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/pointsDeliveryDate',
          component: 'datepicker',
          className: 'box-size-width z-9',
          placeholder: 'Choose date',
          label: 'Point Delivery Date*',
          isDark: true,
          required: false,
          dependentDate: targetEndDate,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {
              type: DATE_VALIDATION_TYPE.START_DATE,
              msg: ` Point Delivery Date cannot be in the past.`,
              excludeLabel: true,
            },
            {
              type: DATE_VALIDATION_TYPE.START_DATE_GREATER,
              startDate: pointsDeliveryDate,
              endDate: targetEndDate,
              msg: ' Point delivery date should be after target delivery date',
              excludeLabel: true,
            },
          ],
        },
      ],
    },
  ],
})

export const prerequisiteJsonSchema = {
  type: 'object',
  properties: {
    isPrerequisite: {
      type: 'boolean',
    },
    chooseTarget: {
      type: 'string',
    },
  },
  required: [],
}

export const prerequisiteUiSchema = {
  type: 'HorizontalLayout',
  className: 'filters-partner',
  elements: [
    {
      type: 'Control',
      scope: '#/properties/isPrerequisite',
      component: 'toggle',
      label: 'Prerequisite',
      className: 'toggle-width',
      isDark: true,
      showCheckedIcon: false,
    },
    {
      type: 'Control',
      scope: '#/properties/chooseTarget',
      component: 'button',
      label: 'Choose Target',
    },
  ],
}

export const additionalJsonSchema = {
  type: 'object',
  properties: {
    userNeedsToJoin: {type: 'boolean'},
    excludeTaxFromGoal: {type: 'boolean'},
    includeOfflineHours: {type: 'boolean'},
    roundUpPointsFlag: {type: 'boolean'},
  },
  required: [],
}
export const additionalUiSchema = {
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'additional-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/userNeedsToJoin',
          component: 'toggle',
          label: 'Users need to join this campaign?',
          isDark: true,
          showCheckedIcon: false,
        },
        {
          type: 'Control',
          scope: '#/properties/excludeTaxFromGoal',
          component: 'toggle',
          label: 'Exclude tax from goal?',
          isDark: true,
          showCheckedIcon: false,
        },
        {
          type: 'Control',
          scope: '#/properties/includeOfflineHours',
          component: 'toggle',
          label: 'Include Offline Orders?',
          isDark: true,
          showCheckedIcon: false,
        },
      ],
    },
    {
      type: 'HorizontalLayout',
      className: 'avail-second-round',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/roundUpPointsFlag',
          component: 'toggle',
          label: 'Round up points?',
          isDark: true,
          showCheckedIcon: false,
        },
      ],
    },
  ],
}

export const roundUpJsonSchema = {
  type: 'object',
  properties: {
    roundOffValue: {
      type: 'string',
    },
  },
  required: [],
}

export const roundUpUiSchema = {
  type: 'VerticalLayout',
  className: 'filter-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'avail-second-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/roundOffValue',
          component: 'textfield',
          label: 'Round Up Amount*',
          className: 'box-size-width',
          placeholder: 'Round Up Amount',
          isDark: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'number', msg: ` Please enter a valid numeric round up amount`, excludeLabel: true},
          ],
        },
      ],
    },
  ],
}
export const fileJsonSchema = {
  type: 'object',
  properties: {
    files: {
      type: 'string',
      format: 'data-url',
      title: 'files',
    },
  },
  required: ['files'],
}

export const fileUiSchema = {
  type: 'HorizontalLayout',
  className: 'filters-partner',
  elements: [
    {
      type: 'Control',
      scope: '#/properties/files',
      component: 'FileUploadController',
    },
  ],
}

export const activeAndDisplayJsonSchema = {
  type: 'object',
  properties: {
    displayStartDate: {
      type: 'string',
    },
    displayEndDate: {
      type: 'string',
    },
  },
  required: [],
}
export const activeAndDisplayUiSchema = (displayStartDate, displayEndDate, activeDisplayDate, activeEndDate) => ({
  type: 'VerticalLayout',
  className: 'filters-partners',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'avail-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/displayStartDate',
          component: 'datepicker',
          className: 'box-size-width z-35',
          placeholder: 'Choose date',
          label: 'Display Start Date *',
          dependentDate: activeDisplayDate,
          isDark: true,
          required: false,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {
              type: DATE_VALIDATION_TYPE.END_DATE_GREATER,
              msg: ` Display date can be on or before active start date`,
              endDate: activeDisplayDate,
              excludeLabel: true,
            },
            {
              type: DATE_VALIDATION_TYPE.START_DATE,
              msg: ` Display date cannot be in the past`,
              endDate: activeDisplayDate,
              excludeLabel: true,
            },
          ],
        },
        {
          type: 'Control',
          scope: '#/properties/displayEndDate',
          component: 'datepicker',
          placeholder: 'Choose date',
          className: 'box-size-width z-25',
          label: 'Display End Date *',
          dependentDate: [displayStartDate, activeEndDate],
          disabled: !displayStartDate ? true : false,
          disabledHelperText: ' Please select start date before selecting end date.',
          isDark: true,
          required: false,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {
              type: DATE_VALIDATION_TYPE.END_DATE,
              msg: ` Display end date can be on or after the active end date`,
              startDate: activeEndDate,
              excludeLabel: true,
            },
          ],
        },
      ],
    },
  ],
})
